import React from "react";
import { StyleSheet, View, Text } from "react-native";
import Icon from "src/components/Icon";

const CC_BRANDS = {
  visa: {
    name: "VISA",
    color: "#0D4F9E",
    icon: "8e15c027-4ec8-4c87-b82d-82081f989b39"
  },
  mastercard: {
    name: "MASTERCARD",
    color: "#363636",
    icon: "1b2ac297-44b3-42ea-86ad-b6a86ec4aa97"
  },
  amex: {
    name: "AMEX",
    color: "#006FCF",
    icon: "e39bd571-8880-4019-9ec3-3fc1ce9b41d7"
  },
  discover: {
    name: "DISCOVER",
    color: "#F27712",
    icon: "0c183fb6-3a11-41e2-9ed3-72174e93ef93"
  },
  diners: {
    name: "DINERS CLUB",
    color: "#0079BE",
    icon: "cc81e7ce-5e53-459d-b9e3-2c2009acb903"
  },
  jcb: {
    name: "JCB",
    color: "#0B4EA2",
    icon: "77437a63-99b3-46d4-8c86-a0bf390a576b"
  },
  unionpay: {
    name: "UNIONPAY",
    color: "#00447C",
    icon: "3550c565-778d-471f-bf15-553fb5e79cff" // credit card
  },
  default: {
    name: "",
    color: "#505050",
    icon: "3550c565-778d-471f-bf15-553fb5e79cff" // credit card
  }
};

function getCreditCardColor(brand) {
  return (CC_BRANDS[brand?.toLowerCase()] || CC_BRANDS.default).color;
}

function getCreditCardIcon(brand) {
  return (CC_BRANDS[brand?.toLowerCase()] || CC_BRANDS.default).icon;
}

function getCreditCardName(brand) {
  return (CC_BRANDS[brand?.toLowerCase()] || CC_BRANDS.default).name;
}

function renderPaymentMethodTitle(paymentMethod) {
  if (paymentMethod.type === "card") {
    return (
      <>
        {(paymentMethod.card?.brand || "").toUpperCase()}
        {" •••• "}
        {paymentMethod.card?.last4}
      </>
    );
  }

  if (paymentMethod.type === "us_bank_account") {
    return (
      <>
        {paymentMethod.us_bank_account?.bank_name || "BANK"}
        {" •••• "}
        {paymentMethod.us_bank_account?.last4}
      </>
    );
  }

  return `PAYMENT METHOD (${paymentMethod.type})`;
}

const PaymentMethod = ({ paymentMethod }) => {
  if (paymentMethod.type === "card") {
    return (
      // Credit Card Style UI
      <View style={[styles.container, { maxWidth: 360 }]}>
        <View
          style={[
            styles.creditCardContainer,
            { backgroundColor: getCreditCardColor(paymentMethod.card?.brand) }
          ]}
        >
          <View style={styles.cardBrandContainer}>
            <View style={styles.brandWithIconContainer}>
              <Icon
                icon={getCreditCardIcon(paymentMethod.card?.brand)}
                size={20}
                color="white"
                accessibilityLabel={`${paymentMethod.card?.brand ||
                  "Credit"} card icon`}
              />
              <Text style={styles.cardBrandText}>
                {getCreditCardName(paymentMethod.card?.brand)}
              </Text>
            </View>
            <View style={styles.cardTypeChip}>
              <Text style={styles.cardTypeText}>
                {(paymentMethod.card?.funding || "").toUpperCase()}
              </Text>
            </View>
          </View>

          <Text style={styles.cardNumberText}>
            •••• •••• •••• {paymentMethod.card?.last4}
          </Text>

          <View style={styles.cardBottomRow}>
            <View>
              <Text style={styles.cardLabelText}>CARD HOLDER</Text>
              <Text style={styles.cardValueText}>
                {paymentMethod.billing_details?.name || "UNSPECIFIED"}
              </Text>
            </View>
            <View>
              <Text style={styles.cardLabelText}>EXPIRES</Text>
              <Text style={styles.cardValueText}>
                {paymentMethod.card?.exp_month}/{paymentMethod.card?.exp_year}
              </Text>
            </View>
          </View>
        </View>
      </View>
    );
  }
  if (paymentMethod.type === "us_bank_account") {
    return (
      // Bank Account Style UI
      <View style={styles.container}>
        <View style={styles.bankAccountContainer}>
          <View style={styles.bankHeaderRow}>
            <View style={styles.bankLogoContainer}>
              <View style={styles.bankLogo}>
                <Text style={styles.bankLogoText}>
                  {(
                    paymentMethod.us_bank_account?.bank_name || "BANK"
                  ).substring(0, 1)}
                </Text>
              </View>
            </View>
            <View style={styles.bankInfo}>
              <Text style={styles.bankName}>
                {paymentMethod.us_bank_account?.bank_name || "BANK"}
              </Text>
              <Text style={styles.accountType}>
                {(paymentMethod.us_bank_account?.account_type || "")
                  .charAt(0)
                  .toUpperCase() +
                  (paymentMethod.us_bank_account?.account_type || "").slice(
                    1
                  )}{" "}
                Account
              </Text>
            </View>
          </View>

          <View style={styles.bankDetailsContainer}>
            <View style={styles.bankDetail}>
              <Text style={styles.bankDetailLabel}>ACCOUNT</Text>
              <Text style={styles.bankDetailValue}>
                ••••••••{paymentMethod.us_bank_account?.last4}
              </Text>
            </View>

            <View style={styles.bankDetail}>
              <Text style={styles.bankDetailLabel}>ROUTING</Text>
              <Text style={styles.bankDetailValue}>
                ••••{paymentMethod.us_bank_account?.routing_number?.slice(-4)}
              </Text>
            </View>

            {paymentMethod.billing_details?.name && (
              <View style={styles.bankDetail}>
                <Text style={styles.bankDetailLabel}>ACCOUNT HOLDER</Text>
                <Text style={styles.bankDetailValue}>
                  {paymentMethod.billing_details.name}
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>
    );
  }
  // Default Payment Method View for other types
  return (
    <View style={styles.container}>
      <View style={styles.paymentMethodHeader}>
        <Text style={styles.paymentMethodTitle}>
          {renderPaymentMethodTitle(paymentMethod)}
        </Text>
        <View style={styles.badgeContainer}>
          <Text style={styles.badgeText}>DEFAULT</Text>
        </View>
      </View>

      <View style={styles.paymentMethodDetails}>
        {paymentMethod.billing_details?.address?.postal_code && (
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Postal Code</Text>
            <Text style={styles.detailValue}>
              {paymentMethod.billing_details.address.postal_code}
            </Text>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 10
  },
  paymentMethodHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12
  },
  paymentMethodTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2c2c2c"
  },
  badgeContainer: {
    backgroundColor: "#23a3ff",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4
  },
  badgeText: {
    color: "white",
    fontSize: 10,
    fontWeight: "bold"
  },
  paymentMethodDetails: {
    marginTop: 5
  },
  detailRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 5
  },
  detailLabel: {
    color: "#6c757d",
    fontSize: 14
  },
  detailValue: {
    fontWeight: "500",
    fontSize: 14
  },
  noPaymentMethod: {
    marginTop: 10,
    fontStyle: "italic",
    color: "#6c757d"
  },
  creditCardContainer: {
    padding: 16,
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 3,
    height: 180,
    justifyContent: "space-between"
  },
  cardBrandContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  },
  brandWithIconContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 2
  },
  cardBrandText: {
    color: "white",
    fontSize: 18,
    fontWeight: "700",
    textShadowColor: "rgba(0, 0, 0, 0.3)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
    marginLeft: 8
  },
  cardTypeChip: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4
  },
  cardTypeText: {
    color: "white",
    fontSize: 12,
    fontWeight: "600"
  },
  cardNumberText: {
    color: "white",
    fontSize: 20,
    fontWeight: "500",
    letterSpacing: 2,
    textShadowColor: "rgba(0, 0, 0, 0.3)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2
  },
  cardBottomRow: {
    flexDirection: "row",
    justifyContent: "space-between"
  },
  cardLabelText: {
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: 10,
    fontWeight: "600"
  },
  cardValueText: {
    color: "white",
    fontSize: 14,
    fontWeight: "600",
    textShadowColor: "rgba(0, 0, 0, 0.2)",
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 1
  },
  bankAccountContainer: {
    backgroundColor: "#f8f9fa",
    borderRadius: 12,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "#e2e8f0"
  },
  bankHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    backgroundColor: "#e9eef6",
    borderBottomWidth: 1,
    borderBottomColor: "#dbe4f0"
  },
  bankLogoContainer: {
    marginRight: 12
  },
  bankLogo: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#3b7de9",
    justifyContent: "center",
    alignItems: "center"
  },
  bankLogoText: {
    color: "white",
    fontSize: 20,
    fontWeight: "bold"
  },
  bankInfo: {
    flex: 1
  },
  bankName: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2d3748"
  },
  accountType: {
    fontSize: 14,
    color: "#4a5568"
  },
  bankDetailsContainer: {
    padding: 16
  },
  bankDetail: {
    marginBottom: 12
  },
  bankDetailLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#718096",
    marginBottom: 2
  },
  bankDetailValue: {
    fontSize: 16,
    fontWeight: "500",
    color: "#2d3748"
  }
});

export default PaymentMethod;
